<script setup lang="ts">
import hash from 'object-hash'

import AudienceProfilBlock from './AudiencePage/AudienceProfilBlock.vue'
import AudienceVisitBlock from './AudiencePage/AudienceVisitBlock.vue'
import AudienceEngagmentBlock from './AudiencePage/AudienceEngagmentBlock.vue'
import AudienceProductBlock from './AudiencePage/AudienceProductBlock.vue'
import AudienceAside from './AudiencePage/AudienceAside.vue'
import useRulesMatcher from './composables/rulesMatcher'

import type { AudienceRule, AudienceStore, Rules } from '@/types/types'
import type { AudienceRuleRecord, DateFilterType } from '@/types/audience'

import { debounce } from 'lodash'
import type { Contest } from '../contests/Components/types/global'
import axios from 'axios'
import { isBetweenRuleIncomplete } from '@/utils/audienceRules'
import { collapseFullStoreScope, expandGlobalStoreScope } from '@/utils/audienceStoreIds'

const { fetchAudienceRules, fetchEstimateAudience, formatAudienceRulesRecordToArray } = useAudience()
const { getTagBy } = useCustomer()
const { t } = useI18n()
const notification = useNotification()
const route = useRoute()

const props = defineProps<{
  audienceId?: number
  isUpdate?: boolean
  isEditing?: boolean
  initialRules?: { [key in Rules]: AudienceRule }
  initialStoreIds?: number[]
  merchantStores?: AudienceStore[]
  scopedStores?: AudienceStore[]
  hideStoreScope?: boolean
}>()

const emit = defineEmits<{
  (e: 'updateRules', value: Record<string, AudienceRule>): void
  (e: 'update:audienceCustomersNewCount', value: number): void
  (e: 'update:storeIds', value: number[]): void
  (e: 'hasRulesError', value: boolean): void
}>()

const { stores } = useLocalStorage()

const merchantStoreIds = computed(() => (props.merchantStores ?? []).map((store) => store.id))

// `storeIds` is what the picker shows (a global audience = every store checked),
// `scopeStoreIds` what the API stores (every store checked = global).
const storeIds = ref<number[]>(expandGlobalStoreScope(props.initialStoreIds ?? [], merchantStoreIds.value))
const scopeStoreIds = computed(() => collapseFullStoreScope(storeIds.value, merchantStoreIds.value))

watch([() => props.initialStoreIds, merchantStoreIds], ([newStoreIds]) => {
  if (newStoreIds && hash(collapseFullStoreScope(newStoreIds, merchantStoreIds.value)) !== hash(scopeStoreIds.value)) {
    storeIds.value = expandGlobalStoreScope(newStoreIds, merchantStoreIds.value)
  } else if (scopeStoreIds.value.length === 0) {
    storeIds.value = expandGlobalStoreScope([], merchantStoreIds.value)
  }
})

watch(scopeStoreIds, (newScopeStoreIds, previousScopeStoreIds) => {
  if (hash(newScopeStoreIds) === hash(previousScopeStoreIds ?? null)) return
  emit('update:storeIds', [...newScopeStoreIds])
  fetchTargets()
})

// Stores outside the user's scope aren't in the local store list: resolve their names from the API payload.
const knownStores = computed(() => [
  ...(props.merchantStores ?? []),
  ...(props.scopedStores ?? []),
  ...(stores.value ?? [])
])

const selectedStores = computed(() =>
  scopeStoreIds.value.map((id) => ({
    id,
    name: knownStores.value.find((store) => store.id === id)?.name ?? String(id)
  }))
)

const merchantStoreOptions = computed(() =>
  (props.merchantStores ?? []).map((store) => ({ value: store.id, label: store.name }))
)

// The "in your stores" breakdown only adds information when the audience reaches beyond them.
const isScopeWithinUserStores = computed(
  () =>
    scopeStoreIds.value.length > 0 &&
    scopeStoreIds.value.every((id) => (stores.value ?? []).some((store) => store.id === id))
)

const defaultRules: { [key: string]: AudienceRule } = {
  gender: { value: [], operator: 'in' },
  age: { value: [], operator: 'exist' },
  firstName: { value: [], operator: 'exist' },
  lastName: { value: [], operator: 'exist' },
  email: { value: [], operator: 'exist' },
  birthDate: { value: [], operator: 'exist' },
  optin: { value: [], operator: 'in' },
  hasOptinEmail: { value: [], operator: 'in' },
  canReceivePushNotifications: { value: [], operator: 'eq' },
  country: { value: [], operator: 'in' },
  city: { value: [], operator: 'exist' },
  registrationDate: { value: [], operator: 'eq' },
  lastValidCheckin: { value: [], operator: 'eq' },
  checkinCount: {
    value: [],
    operator: 'eq',
    parameters: {
      date_filter: {
        value: [],
        type: 'absolute' as DateFilterType,
        operator: 'eq'
      }
    }
  },
  averageCheckinCount: { value: [], operator: 'eq' },
  pointTotal: { value: [], operator: 'eq' },
  pointCumulated: {
    value: [],
    operator: 'eq',
    parameters: {
      date_filter: {
        value: [],
        type: 'absolute' as DateFilterType,
        operator: 'eq'
      }
    }
  },
  consumedProduct: {
    value: [],
    operator: 'in',
    parameters: {
      date_filter: {
        value: [],
        type: 'absolute' as DateFilterType,
        operator: 'eq'
      }
    }
  },
  contestGameParticipation: { value: [], operator: 'in' },
  surveys: { value: [], operator: 'in' },
  tags: { value: [], operator: 'in' },
  webappLogIn: { value: [], operator: 'eq' }
}

const rules = ref<{ [key: string]: AudienceRule }>({ ...defaultRules })
watch(
  () => props.initialRules,
  (newInitialRules) => {
    if (newInitialRules) {
      rules.value = { ...defaultRules, ...newInitialRules }
    }
  },
  { immediate: true }
)

const lastEstimatedAudiencePayloadHash = ref(null)

const isLoading = ref(true)
const isTargetLoading = ref(false)
const optinTargets = ref<number | null>(null)
const userStoresAudience = ref<number | null>(null)
const userStoresCount = ref<number | null>(null)

const shouldDisplayAudienceProductBlock = ref(false)

const hasRulesError = ref(false)
const hasIncompleteAgeBetweenRule = computed(() => isBetweenRuleIncomplete(rules.value.age))
const hasBlockingRulesError = computed(() => hasRulesError.value || hasIncompleteAgeBetweenRule.value)

let cancelFetchEstimatedAudience

const fetchTargets = debounce(async () => {
  if (hasBlockingRulesError.value) {
    if (cancelFetchEstimatedAudience) {
      cancelFetchEstimatedAudience.cancel()
    }
    isTargetLoading.value = false
    return
  }

  if (cancelFetchEstimatedAudience) {
    cancelFetchEstimatedAudience.cancel()
  }

  cancelFetchEstimatedAudience = axios.CancelToken.source()

  isTargetLoading.value = true

  try {
    const audienceRulesFormatted = formatAudienceRulesRecordToArray(rules.value) as AudienceRule[]
    const newHash = hash({ rules: audienceRulesFormatted, storeIds: scopeStoreIds.value })
    if (newHash === lastEstimatedAudiencePayloadHash.value) {
      return
    }
    lastEstimatedAudiencePayloadHash.value = newHash

    const response = await fetchEstimateAudience(
      audienceRulesFormatted,
      scopeStoreIds.value,
      cancelFetchEstimatedAudience?.token
    )

    if (!response) {
      return
    }

    optinTargets.value = response.audience
    userStoresAudience.value = response.userStoresAudience
    userStoresCount.value = response.userStoresCount
    emit('update:audienceCustomersNewCount', optinTargets.value)
  } catch (error) {
    notification.create('error', {
      text: t('pages.customer.audienceDetailPage.notifications.loading.estimated.error'),
      persist: true
    })
    optinTargets.value = 0
    throw new Error(`Error fetching estimated audience: ${error}`)
  } finally {
    isTargetLoading.value = false
  }
}, 500)

const fetchAudienceProductBlockAvailability = async () => {
  try {
    const response = await axios.get('/partner-parameters', {
      params: { isActive: 1, hasConsumedProductsSupport: 1 }
    })
    const items = response?.data?.data ?? []

    shouldDisplayAudienceProductBlock.value = items.length > 0
  } catch {
    shouldDisplayAudienceProductBlock.value = false
  }
}

const getAudienceRules = async () => {
  if (!props.audienceId) return

  try {
    const audienceRulesRecord = (await fetchAudienceRules(props.audienceId)) as AudienceRuleRecord

    rules.value = { ...defaultRules, ...audienceRulesRecord }
  } catch {
    notification.create('error', {
      text: t('pages.customer.audienceDetailPage.notifications.loading.error')
    })
  } finally {
    isLoading.value = false
  }
}

watch(
  rules,
  () => {
    emit('updateRules', rules.value)
    fetchTargets()
  },
  { deep: true }
)

const { getHighlightInfo } = useRulesMatcher(rules)

const games = ref<Contest[]>([])
provide('games', games)
const surveys = ref([])
provide('surveys', surveys)
const consumedProducts = ref<any[]>([])
provide('consumedProducts', consumedProducts)
provide('selectedStores', selectedStores)
const channelTags = getTagBy('type', 'channel')
provide('channelTags', channelTags)
const marketingTags = getTagBy('type', 'marketing')
provide('marketingTags', marketingTags)

const updateGames = (newGames: Contest[]) => {
  games.value = newGames
}

const updateSurveys = (newSurveys: any[]) => {
  surveys.value = newSurveys
}

const classes = {
  fieldset: {
    container: 'space-y-3',
    legend: 'text-lg font-semibold leading-relaxed text-foreground',
    block: 'space-y-5 rounded-md border bg-white p-5'
  },
  rowGrid: 'grid grid-cols-12 gap-4 items-center',
  colGrid: 'grid grid-cols-2 gap-4',
  label: 'col-span-5 flex flex-wrap break-words text-sm font-medium leading-normal text-foreground',
  inputs: 'col-span-7 grid grid-cols-2 gap-4',
  checkboxesContainer: 'col-span-7 flex flex-wrap items-center justify-start gap-4'
}

const handleRulesError = (newHasRulesError: boolean) => {
  hasRulesError.value = newHasRulesError
}

watch(
  hasBlockingRulesError,
  (newHasBlockingRulesError) => {
    emit('hasRulesError', newHasBlockingRulesError)
  },
  { immediate: true }
)

onMounted(async () => {
  await fetchAudienceProductBlockAvailability()

  if (props.initialRules) {
    await fetchTargets()
    isLoading.value = false
  } else {
    if (!props.isUpdate && route.query.name) {
      isLoading.value = false
    } else if (props.audienceId) {
      await getAudienceRules()
    }
  }
})
</script>

<template>
  <section v-if="!isLoading" class="mb-6 flex flex-col-reverse gap-4 xl:grid xl:grid-cols-3">
    <div class="col-span-1 lg:col-span-2">
      <div class="space-y-3">
        <AudienceProfilBlock
          v-model:store-ids="storeIds"
          :has-store-scope="scopeStoreIds.length > 0"
          :additional-store-options="merchantStoreOptions"
          :hide-store-scope="hideStoreScope"
          :disabled="isEditing ? false : isUpdate ? false : true"
          :rules="rules"
          :classes="classes"
          :get-highlight-info="getHighlightInfo"
          @has-rules-error="handleRulesError"
        />
        <AudienceVisitBlock
          :disabled="isEditing ? false : isUpdate ? false : true"
          :rules="rules"
          :classes="classes"
          :get-highlight-info="getHighlightInfo"
          @has-rules-error="handleRulesError"
        />
        <AudienceEngagmentBlock
          :disabled="isEditing ? false : isUpdate ? false : true"
          :rules="rules"
          :classes="classes"
          :get-highlight-info="getHighlightInfo"
          @update:games="updateGames"
          @update:surveys="updateSurveys"
          @has-rules-error="handleRulesError"
        />
        <AudienceProductBlock
          v-if="shouldDisplayAudienceProductBlock"
          :disabled="isEditing ? false : isUpdate ? false : true"
          :rules="rules"
          :classes="classes"
          :get-highlight-info="getHighlightInfo"
          @update:consumedProducts="(list) => (consumedProducts = list)"
        />
      </div>
    </div>
    <div class="col-span-1">
      <AudienceAside
        class="sticky top-2"
        :rules="rules"
        :optin-targets="optinTargets"
        :is-scope-within-user-stores="isScopeWithinUserStores"
        :stores-contacts-count="userStoresAudience"
        :stores-count="userStoresCount"
        :loading="isTargetLoading"
        :is-update="audienceId ? true : false"
      />
    </div>
  </section>
  <TheLoadingPage v-else :has-header="false" />
</template>
